<script lang="ts">
	//Hooks
	import { beforeUpdate, onMount } from 'svelte';
	//Services
	import { format } from 'svelte-i18n';
	//Configs
	import { devices_config } from '@devices/index';
	//Helpers
	import {
		get_mode,
		is_gen_2,
		isRgbCct,
		load_image_from,
		real_id,
		isCctOnlyDevice,
		isXMODDevice,
		isRgbwwPm,
	} from '../../helpers/interface_helpers';
	//Stores
	import { devices_settings } from 'src/stores/device-runtime/status';
	import {
		createMemberSettingsSnapshot,
		createMemberStatusSnapshot,
	} from 'src/stores/device-runtime/status-selectors';
	import { devices } from 'src/stores/lists/devices';
	import { groups } from 'src/stores/lists/groups';
	import { consumption_by_device_account } from 'src/stores/features/consumption';
	import { navigateToDevice } from 'src/stores/ui/navigate-to';
	//Design system components
	import {
		Alert,
		DrawerTabs,
		DrawerTab,
		DrawerTabContent,
		IconButton,
		Stack,
		ListItem,
		Text,
	} from '@Shelly/shelly-design-system';
	//Components
	import ControlGroupOnOff from '../Controls/Group/ControlGroupOnOff.svelte';
	import ControlGroupColor from '../Controls/Group/ControlGroupColor.svelte';
	import ControlGroupWhiteChannel from '../Controls/Group/ControlGroupWhiteChannel.svelte';
	import ControlGroupGainBrightness from '../Controls/Group/ControlGroupGainBrightness.svelte';
	import ControlGroupColorTemp from '../Controls/Group/ControlGroupColorTemp.svelte';
	import ControlGroupMode from '../Controls/Group/ControlGroupMode.svelte';
	import ControlGroupDirection from '../Controls/Group/ControlGroupDirection.svelte';
	import ControlGroupPosition from '../Controls/Group/ControlGroupPosition.svelte';
	import ControlGroupEffects from '../Controls/Group/ControlGroupEffects.svelte';
	import ControlGroupThermostat from '../Controls/Group/ControlGroupThermostat.svelte';

	import GroupDevicesListScrollable from '../Groups/GroupDevicesListScrollable.svelte';
	import GroupDevicesList from '../Groups/GroupDevicesList.svelte';
	import AddEditGroupWizard from '../Groups/GroupWizard/AddEditGroupWizard.svelte';

	import EventLog from '../EventLog/EventLog.svelte';

	import { convert } from '../../helpers/utilities';
	import { toggleDrawerState } from 'src/helpers/drawer_helpers';
	import {
		defaultGroupObject,
		isGroupModeChangeEligible,
		getModeChangeBlockingType,
	} from 'src/helpers/group_helpers';
	import { supportsColorModeChange } from 'src/helpers/night_mode_helpers';
	import { isSubAccount } from 'src/helpers/user_helpers';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';

	let id = $sidePanelStateCombined?.id;
	let group = { ...$groups[id], devices: [...new Set(($groups[id]?.devices ?? []).map(String))] };

	$: image = group?.image;
	$: backgroundColor = group?.backgroundColor;
	$: type = group?.type;
	$: groupDevices = group?.devices;
	$: memberStatusStore = createMemberStatusSnapshot(groupDevices ?? []);
	$: memberSettingsStore = createMemberSettingsSnapshot(groupDevices ?? []);
	$: memberStatus = $memberStatusStore;
	$: memberSettings = $memberSettingsStore;
	$: name = group?.name;

	$: list = groupDevices
		?.map((id: string) => {
			let baseId = id;
			let channel = 0;
			if (id.includes('_')) {
				const idMatch = id.match(/^(.+)_(\d+)$/);
				if (idMatch) {
					baseId = idMatch[1];
					channel = parseInt(idMatch[2], 10);
				}
			}
			if ($devices[baseId]) {
				const channelDevice = $devices[id];
				const baseDevice = $devices[baseId];
				const device = channelDevice
					? { ...baseDevice, ...channelDevice, id, channel }
					: { ...baseDevice, id, channel };
				return device;
			}
			if (!$devices[baseId]) {
				return { id: id, deleted: true };
			}
		})
		?.filter((e) => e !== undefined);

	let lights_category_devices = [];
	let color_mode_lights = [];
	let white_channel_support_lights = [];
	let white_mode_lights = [];
	let change_mode_lights = [];
	$: active_mode = 'color';
	let effects_supported_lights = [];
	let saved_effects_enabler = false;
	let roller_position_devices = [];
	let hasRGBW2 = false;
	let isGroupModeSwitchEligible = false;
	let modeChangeBlockingType: string | null = null;

	type ActiveGroup = {
		name: string;
		drawerTabItems: {
			title: string;
			image: string;
			backgroundColor?: any;
			components: any;
			disabled: boolean;
			notification?: boolean;
		}[];
	};

	$: activeGroup = {
		name: name,
		drawerTabItems: [
			{
				title: 'main_controls',
				image: image ? load_image_from(image) : undefined,
				backgroundColor: backgroundColor,
				disabled: false,
				notification: undefined,
			},
		],
	} as ActiveGroup;

	let activeDeviceTabIndex = 0;

	let showAddEditGroupModal = false;
	const units = 'W';
	const defaultToShow = 10;
	let showAllDevices = false;

	$: pm_group_devices = groupDevices?.filter((id) => {
		return devices_config[$devices[id]?.type]?.isPM;
	});
	$: consumingDevices =
		pm_group_devices?.length === 0
			? []
			: Object.keys($consumption_by_device_account)
					.filter((dev: string) => {
						return (
							pm_group_devices?.includes(dev as never) &&
							$consumption_by_device_account[dev]?._power !== 0
						);
					})
					.map((devID) => {
						const { image, name } = $devices?.[devID];
						return {
							...$consumption_by_device_account[devID],
							image,
							name,
							devID,
						};
					})
					.sort((a, b) => {
						// order devices by consumption
						return b._power - a._power;
					});
	$: hideShowMoreItem = consumingDevices.length - defaultToShow <= 0;
	$: devicesToRender = showAllDevices ? consumingDevices : consumingDevices.slice(0, defaultToShow);

	onMount(async () => {
		if (type === 'light') {
			activeGroup.drawerTabItems.push({
				title: 'secondary_controls',
				image: null,
				components: null,
				disabled: color_mode_lights.length < 1 && white_mode_lights.length < 1,
			});
			activeGroup.drawerTabItems.push({
				title: 'effects',
				image: null,
				components: null,
				disabled: !saved_effects_enabler,
			});

			//***Will leave this for future ideas :D
			// activeGroup.drawerTabItems.splice(3, 0, {
			// 	title: 'white_mode_control',
			// 	disabled: white_mode_lights.length < 1
			// });
		}
		if (type === 'light' || type === 'roller') {
			activeGroup.drawerTabItems.splice(2, 0, {
				//moving devices tab after secondary controls
				title: 'devices',
				image: null,
				components: null,
				disabled: false,
			});
			/** add event log for all groups types without thermostats*/
			// if (['light', 'relay', 'roller'].includes(type)) {
			// 	activeGroup.drawerTabItems.push({
			// }

			/** add actively consuming tab for all relay and light groups and disable it if there is no any device with power meter*/
			// if (type === 'relay' || type === 'light') {
			// 	activeGroup.drawerTabItems.push({
			// 		title: 'actively_consuming',
			// 		disabled: pm_group_devices.length === 0,
			// 	});
			// }

			activeGroup = activeGroup;
		}
	});

	enum IconVariants {
		secondary_controls = 'fa-lightbulb',
		devices = 'fa-microchip',
		devices_small = 'fa-microchip',
		effects = 'fa-stars',
		event_log = 'fa-notebook',
		actively_consuming = 'fa-bolt',
		// white_mode_control = 'fa-brightness'
	}

	const getIconFromTitle = (title: string) => {
		const icon = IconVariants[title];
		if (icon == undefined) {
			// console.warn('Missing icon for title:', title);
			return '';
		}
		return 'fal ' + icon;
	};

	const getActiveIconDrawerTab = (icon: string) => {
		return icon ? `fas ${icon.split(' ')[1]} text-primary` : undefined;
	};

	const getIconDrawerTab = (tabData, index) => {
		return tabData?.image
			? ''
			: activeDeviceTabIndex === index
				? getActiveIconDrawerTab(getIconFromTitle(tabData.title))
				: getIconFromTitle(tabData.title);
	};

	beforeUpdate(() => {
		/**map all devices and return new array for brightness control, where we store current operation mode + slider type from config
		 * if there is no set up mode, it will redirect to device config family, so it must return 'light'
		 **/

		if (type === 'light') {
			lights_category_devices = list
				?.filter((device) => {
					const isLightCategory = device.category === 'light' || isCctOnlyDevice(device.type);
					let isXMODLight = false;
					if (isXMODDevice(device?.type)) {
						const realId = real_id(device.id);
						const status = memberStatus[realId];
						if (
							status?.sys?.ch &&
							Array.isArray(status.sys.ch) &&
							status.sys.ch.length > 0
						) {
							const channelIndex = device.channel ?? 0;
							const component = status.sys.ch[channelIndex] || status.sys.ch[0];
							if (component) {
								const componentType = component.split(':')[0];
								isXMODLight =
									componentType === 'light' ||
									componentType === 'cct' ||
									componentType === 'rgbw';
							}
						}
					}
					return (isLightCategory || isXMODLight) && memberStatus[real_id(device.id)]?.online;
				})
				.map((d) => {
					let mode = '';
					if (isXMODDevice(d?.type)) {
						const realId = real_id(d.id);
						const status = memberStatus[realId];
						if (
							status?.sys?.ch &&
							Array.isArray(status.sys.ch) &&
							status.sys.ch.length > 0
						) {
							const channelIndex = d.channel ?? 0;
							const component = status.sys.ch[channelIndex] || status.sys.ch[0];
							if (component) {
								const componentType = component.split(':')[0];
								if (componentType === 'cct') {
									mode = 'cct';
								} else if (componentType === 'rgbw' || componentType === 'light') {
									mode = 'rgbw';
								} else {
									mode = 'shell';
								}
							} else {
								mode = d.mode || 'shell';
							}
						} else {
							mode = d.mode || 'shell';
						}
					} else if (memberStatus[real_id(d.id)] && memberSettings[real_id(d.id)]) {
						const baseDeviceId = real_id(d.id);
						mode = get_mode(
							$devices[baseDeviceId],
							memberStatus[baseDeviceId],
							memberSettings[baseDeviceId],
						);
					} else {
						const baseDeviceId = real_id(d.id);
						mode = Object.keys(devices_config[$devices[baseDeviceId]?.type]?.modes || {})[0];
					}

					// don't override mode for Pro RGBWW PM
					mode = isRgbwwPm(d.type) ? d.mode : mode;

					let slider;
					if (isXMODDevice(d?.type)) {
						if (mode === 'rgbw' || mode === 'cct') {
							slider = 'brightness';
						} else {
							slider = devices_config[d?.type]?.modes?.[mode]?.slider;
						}
					} else {
						slider = devices_config[d?.type]?.modes?.[mode]?.slider;
					}

					return {
						...d, //device must be first, because overrides mode with color everytime
						mode,
						slider,
					};
				});

			color_mode_lights = lights_category_devices?.filter((d) => {
				const mode = get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]);
				const _isRgbCct =
					isRgbCct(d.type) &&
					devices_settings.getByRealId(d.id)?.[`${mode}:0`]?.mode === 'rgb';
				return (
					d.mode === 'color' ||
					d.mode === 'rgb' ||
					d.mode === 'rgbw' ||
					mode === 'rgb' ||
					mode === 'rgbw' ||
					_isRgbCct
				);
			});

			//rgbw 2 color mode -> white channel
			white_channel_support_lights = color_mode_lights?.filter((device) => {
				const mode = get_mode(
					device,
					memberStatus[real_id(device.id)],
					memberSettings[real_id(device.id)],
				);
				return devices_config[device?.type]?.modes?.[mode]?.has_white;
			});

			white_mode_lights = lights_category_devices?.filter((d) => {
				const mode = get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)]);
				return (
					(d.mode === 'white' || d.mode === 'cct') &&
					devices_config[d?.type]?.modes?.[mode]?.color_temp
				);
			});
			hasRGBW2 = lights_category_devices?.some((d) => d.type.includes('RGBW2'));

			change_mode_lights = lights_category_devices?.filter((d) => {
				return supportsColorModeChange(d.type) && memberStatus[real_id(d.id)];
			});

			isGroupModeSwitchEligible = isGroupModeChangeEligible(lights_category_devices);
			modeChangeBlockingType = getModeChangeBlockingType(lights_category_devices);

			active_mode = white_mode_lights?.length > color_mode_lights?.length ? 'white' : 'color';

			//will exclude rgbw2 in white mode, but still support all other lights (color bulbs allow users to change effect even if the bulb is in white mode)
			effects_supported_lights = lights_category_devices?.filter((d) => {
				return (
					memberStatus[real_id(d.id)] &&
					devices_config[d.type]?.modes?.[
						get_mode(d, memberStatus[real_id(d.id)], memberSettings[real_id(d.id)])
					]?.effects
				);
			});
			saved_effects_enabler = effects_supported_lights.length > 0;

			activeGroup.drawerTabItems = activeGroup.drawerTabItems.map((item) => {
				if (item.title === 'effects') {
					return {
						...item,
						disabled: !saved_effects_enabler,
					};
				} else if (item.title === 'secondary_controls') {
					return {
						...item,
						disabled: color_mode_lights.length < 1 && white_mode_lights.length < 1,
					};
				} else {
					return item;
				}
			});
		} else if (type === 'roller') {
			roller_position_devices = list.filter((d) => d.category === 'roller');
		} else {
			// do nothing
		}

		//rollers group
	});

	let editGroup = defaultGroupObject;

	const handleGroupEdit = () => {
		editGroup = { ...group };
		showAddEditGroupModal = true;
	};

	const onFinishEdit = () => {
		const activeDrawerTabsTitles = activeGroup.drawerTabItems.map((tab) => tab.title);
		if (type === 'light') {
			!activeDrawerTabsTitles.includes('secondary_controls') &&
				activeGroup.drawerTabItems.push({
					title: 'secondary_controls',
					disabled: color_mode_lights.length < 1 && white_mode_lights.length < 1,
				});
			!activeDrawerTabsTitles.includes('effects') &&
				activeGroup.drawerTabItems.push({
					title: 'effects',
					disabled: !saved_effects_enabler,
				});
		}
		if (type === 'light' || type === 'roller') {
			!activeDrawerTabsTitles.includes('devices') &&
				activeGroup.drawerTabItems.splice(1, 0, {
					title: 'devices',
					disabled: false,
				});
		}
		toggleDrawerState();
	};
</script>

<DrawerTabs backgroundImgSrc="">
	<svelte:fragment slot="tabs">
		{#key activeDeviceTabIndex}
			{#if activeGroup.drawerTabItems}
				{#each activeGroup.drawerTabItems as drawerTab, index}
					<DrawerTab
						icon={getIconDrawerTab(drawerTab, index)}
						image={drawerTab?.image}
						backgroundColor={drawerTab?.backgroundColor}
						active={activeDeviceTabIndex === index}
						notification={drawerTab.notification}
						on:click={() => {
							activeDeviceTabIndex = index;
						}}
						disabled={drawerTab.disabled}
					/>
				{/each}
			{/if}
		{/key}
	</svelte:fragment>

	{#key activeDeviceTabIndex}
		<DrawerTabContent
			heading={activeGroup.name}
			subHeading={activeGroup.drawerTabItems
				? $format('settings_title_' + activeGroup.drawerTabItems[activeDeviceTabIndex]?.title)
				: ''}
		>
			<svelte:fragment slot="headingButton">
				{#if !isSubAccount()}
					<IconButton
						aria-label={$format('button_edit_group', {
							default: 'Button edit group',
						})}
						slot="headingButton"
						icon="fas fa-pen"
						on:click={handleGroupEdit}
					/>
				{/if}
			</svelte:fragment>
			{#if activeDeviceTabIndex === 0}
				{#if type === 'roller'}
					<ControlGroupPosition {group} />

					<ControlGroupDirection {group} variant="drawer" />
				{:else if type === 'thermostat'}
					<GroupDevicesListScrollable {list} />
					<ControlGroupThermostat {group} variant="drawer" />
				{:else}
					{#if white_channel_support_lights.length > 0}
						<div class="flex-full-height" />
						<ControlGroupWhiteChannel {white_channel_support_lights} type={'horizontal'} />
					{/if}

					{#if lights_category_devices.filter((d) => d.slider !== undefined).length > 0 && white_channel_support_lights.length < 1}
						<ControlGroupGainBrightness
							{lights_category_devices}
							type={'vertical'}
							groupId={group.id}
						/>
					{:else if lights_category_devices.filter((d) => d.slider !== undefined).length > 0}
						<ControlGroupGainBrightness
							{lights_category_devices}
							type={'horizontal'}
							groupId={group.id}
						/>
					{:else if type !== 'light'}
						<GroupDevicesListScrollable {list} />
					{:else}
						<!-- if there is no any data from devices and we have only on/off buttons use default full-height div-->
						<div class="flex-full-height" />
					{/if}
					<ControlGroupOnOff {group} variant="drawer" />
				{/if}
			{:else if activeGroup.drawerTabItems[activeDeviceTabIndex].title === 'secondary_controls'}
				{#if change_mode_lights.length > 0}
					{#if isGroupModeSwitchEligible}
						<ControlGroupMode {change_mode_lights} {hasRGBW2} />
					{:else if modeChangeBlockingType}
						<Alert
							class="mb-m"
							type="info"
							heading={$format('group_mode_unsupported_desc', {
								default:
									"Mode change isn't supported from groups when a {blockingType} device is included.",
								values: { blockingType: modeChangeBlockingType },
							})}
						/>
					{/if}
				{/if}
				{#key active_mode}
					{#if color_mode_lights.length > 0}
						<ControlGroupColor {color_mode_lights} />
					{/if}
					{#if white_mode_lights.length > 0}
						<ControlGroupColorTemp {white_mode_lights} />
					{/if}
				{/key}
			{:else if activeGroup.drawerTabItems[activeDeviceTabIndex].title === 'effects'}
				<ControlGroupEffects
					color_mode_lights={color_mode_lights.filter(
						(d) => !is_gen_2(d), //filtering gen 2 devices, because plus rgbw doesn't support effects
					)}
				/>
			{:else if activeGroup.drawerTabItems[activeDeviceTabIndex].title === 'devices'}
				{#key list}
					<GroupDevicesList devices={list} />
				{/key}
			{:else if activeGroup.drawerTabItems[activeDeviceTabIndex].title === 'actively_consuming'}
				{#if consumingDevices.length > 0}
					<Stack gap="xs" class="mb-m">
						{#each devicesToRender as { devID, name, image, in_power, _power }}
							<ListItem
								image={load_image_from(image)}
								title={name}
								progress={{
									title: convert(_power, units),
									color: in_power !== 0 ? 'solar' : 'warning',
								}}
								viewType="default"
								actionBtn
								on:click={() => {
									navigateToDevice(devID);
								}}
							/>
						{/each}

						{#if !hideShowMoreItem && !showAllDevices}
							<ListItem
								viewType="more"
								title={`+${consumingDevices.length - defaultToShow} more`}
								on:click={() => {
									showAllDevices = true;
								}}
							/>
						{/if}
					</Stack>
				{:else}
					<Text class="mb-m" styleType="body-small">
						{$format('energy_consuming_devices_descr', {
							default: 'When devices consume energy, you will see them listed here',
						})}
					</Text>
				{/if}
			{:else if activeGroup.drawerTabItems[activeDeviceTabIndex].title === 'event_log'}
				<EventLog devicesArr={list} type="group" />
			{/if}
		</DrawerTabContent>
	{/key}
</DrawerTabs>

{#if showAddEditGroupModal}
	<AddEditGroupWizard editMode {editGroup} bind:showAddEditGroupModal on:finish={onFinishEdit} />
{/if}
